import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Loader2, RefreshCw, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useVisibleDestinations } from '../data/catalog';
import { EcoAssistParrot } from './EcoAssistParrot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const START_ACTIONS = [
  ['trip', 'I’m planning a trip'],
  ['booking', 'I have an existing booking/request'],
  ['agency', 'I’m a Travel Agency / Tour Operator'],
  ['supplier', 'I’m a Local Partner / Supplier'],
];

const PRODUCTS = ['Experiences', 'Multi-day Journeys / Packages', 'Accommodation', 'Private Guide', 'Transfers', 'Tailor-made Trip'];
const AGENCY_ACTIONS = [
  ['quote', 'Request a B2B quotation'],
  ['portfolio', 'Explore our destination portfolio'],
  ['distribution', 'Discuss distribution'],
  ['existing', 'Existing agency booking'],
  ['become', 'Become a distribution partner'],
];
const SUPPLIER_TYPES = ['DMC / Tour Operator', 'Local Guide', 'Hotel / Accommodation', 'Transport Provider', 'Experience Provider', 'Other'];
const SUPPLIER_ACTIONS = [
  ['join', 'Join the Eco Collection network'],
  ['respond', 'Respond to a service request'],
  ['availability', 'Update availability'],
  ['rates', 'Submit rates'],
  ['support', 'Existing partnership support'],
];

const initialForm = {
  destination: '', destination_slug: '', product: '', travel_date: '', travelers: '', preferred_language: 'English',
  interests: '', name: '', email: '', whatsapp: '', consent_terms: false, reference: '', agency_action: '',
  company: '', contact: '', country: '', website: '', message: '', supplier_type: '', supplier_action: '',
};

const ButtonList = ({ items, onChoose }) => (
  <div className="grid gap-2">
    {items.map(([value, label]) => (
      <button key={value} type="button" onClick={() => onChoose(value, label)} className="min-h-11 rounded-xl border border-eco-green/15 bg-white px-4 py-2.5 text-left text-sm font-semibold text-eco-ink transition hover:border-eco-gold hover:bg-eco-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco-gold">
        {label}
      </button>
    ))}
  </div>
);

export function EcoAssist() {
  const navigate = useNavigate();
  const destinations = useVisibleDestinations();
  const closeRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState('home');
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const [context, setContext] = useState('');
  const [invitationVisible, setInvitationVisible] = useState(false);
  const [invitationShown, setInvitationShown] = useState(false);

  useEffect(() => {
    if (open || invitationShown) return undefined;
    const invitationTimer = setTimeout(() => {
      setInvitationVisible(true);
      setInvitationShown(true);
    }, 5000);
    return () => clearTimeout(invitationTimer);
  }, [open, invitationShown]);

  useEffect(() => {
    if (!invitationVisible) return undefined;
    const quietTimer = setTimeout(() => setInvitationVisible(false), 4500);
    return () => clearTimeout(quietTimer);
  }, [invitationVisible]);

  useEffect(() => {
    const openFromContext = (event) => {
      setContext(event.detail?.context || '');
      setInvitationVisible(false);
      setInvitationShown(true);
      setOpen(true);
    };
    window.addEventListener('eco:assist-open', openFromContext);
    return () => window.removeEventListener('eco:assist-open', openFromContext);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    window.dispatchEvent(new CustomEvent('eco:lenis-stop'));
    const timer = setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      window.dispatchEvent(new CustomEvent('eco:lenis-start'));
    };
  }, [open]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const restart = () => { setScreen('home'); setForm(initialForm); setError(''); setReference(''); setContext(''); };
  const close = () => { setOpen(false); setError(''); };
  const toggle = () => {
    setInvitationVisible(false);
    setInvitationShown(true);
    setOpen((value) => !value);
  };
  const go = (target) => { close(); navigate(target); };
  const backMap = {
    'trip-destination': 'home', 'trip-product': 'trip-destination', 'trip-details': 'trip-product', 'trip-contact': 'trip-details', 'trip-summary': 'trip-contact',
    booking: 'home', agency: 'home', 'agency-form': 'agency', supplier: 'home', 'supplier-action': 'supplier', 'supplier-form': 'supplier-action', success: 'home',
  };

  const post = async (path, payload) => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'We could not send your request. Please try again.');
      setReference(data.reference || '');
      setScreen('success');
    } catch (err) {
      setError(err.message || 'We could not send your request. Please try again.');
    } finally { setBusy(false); }
  };

  const submitTrip = () => post('/requests', {
    kind: form.product === 'Transfers' ? 'transfer' : form.product === 'Tailor-made Trip' ? 'tailor_made' : 'trip',
    name: form.name.trim(), email: form.email.trim(), whatsapp: form.whatsapp.trim(),
    destination: form.destination, destination_slug: form.destination_slug, product: form.product,
    travel_date: form.travel_date, travelers: form.travelers, preferred_language: form.preferred_language,
    interests: form.interests.split(',').map((item) => item.trim()).filter(Boolean),
    message: form.interests.trim(), source: 'eco_assist', consent_terms: form.consent_terms,
  });

  const submitAgency = () => {
    const action = { quote: 'trade_enquiry', portfolio: 'product_sheet', distribution: 'trade_enquiry', become: 'become_partner' }[form.agency_action];
    return post(`/travel-trade/requests/${action}`, {
      name: `${form.company.trim()} — ${form.contact.trim()}`, email: form.email.trim(), market: form.country.trim(),
      requirements: [form.message.trim(), form.website.trim() ? `Website: ${form.website.trim()}` : ''].filter(Boolean).join('\n'),
      traveller_profile: 'Eco Assist agency enquiry', consent_terms: form.consent_terms,
    });
  };

  const submitSupplier = () => post('/applications', {
    business_name: form.company.trim(), contact_person: form.contact.trim(), email: form.email.trim(), country: form.country.trim(),
    website: form.website.trim(), business_type: form.supplier_type, description: form.message.trim(), offerings: [],
  });

  const chooseStart = (value) => setScreen(value === 'trip' ? 'trip-destination' : value);
  const chooseAgency = (value) => {
    set('agency_action', value);
    if (value === 'existing') go('/login?role=partner&next=%2Fpartner'); else setScreen('agency-form');
  };
  const chooseSupplierAction = (value) => {
    set('supplier_action', value);
    if (value === 'join') setScreen('supplier-form'); else go('/login?role=partner&next=%2Fpartner');
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70] sm:bottom-6 sm:right-6" data-testid="eco-assist-root">
      {open && (
        <section role="dialog" aria-modal="true" aria-labelledby="eco-assist-title" className="mb-3 flex h-[min(680px,calc(100dvh-6rem))] w-[min(390px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-eco-ink/10 bg-white shadow-[0_24px_70px_rgba(18,46,38,.25)]" data-testid="eco-assist-dialog">
          <header className="flex items-center justify-between bg-eco-green px-4 py-3 text-white">
            <div className="flex items-center gap-2.5">
              <EcoAssistParrot className="h-10 w-9 shrink-0 drop-shadow-sm" />
              <div>
              <h2 id="eco-assist-title" className="text-base font-semibold">Eco Assist</h2>
              <p className="text-[11px] text-white/70">Eco Collection Virtual Assistant</p>
              </div>
            </div>
            <button ref={closeRef} type="button" onClick={close} aria-label="Close Eco Assist" className="grid h-10 w-10 place-items-center rounded-full text-white/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco-gold"><X size={19} /></button>
          </header>

          <div className="flex-1 overflow-y-auto bg-eco-cream/60 p-4" aria-live="polite">
            {screen !== 'home' && screen !== 'success' && (
              <button type="button" onClick={() => { setError(''); setScreen(backMap[screen] || 'home'); }} className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-eco-green"><ArrowLeft size={14} /> Back</button>
            )}
            {screen === 'home' && <>
              {context && <p className="mb-3 rounded-xl border border-eco-gold/25 bg-eco-gold/10 px-3 py-2 text-xs font-semibold text-eco-green" data-testid="eco-assist-context">Eco Assist · {context}</p>}
              <div className="mb-4 rounded-2xl rounded-tl-sm bg-white p-4 text-sm leading-relaxed text-eco-ink shadow-sm">
                <p>Hello! I’m Eco Assist, the Eco Collection virtual assistant.</p>
                <p className="mt-2">I can help you plan a journey, manage an existing request, or connect with our partnership team.</p>
                <p className="mt-2 font-semibold">How can I help you today?</p>
              </div>
              <ButtonList items={START_ACTIONS} onChoose={chooseStart} />
            </>}

            {screen === 'trip-destination' && <>
              <h3 className="mb-3 font-semibold text-eco-ink">Where would you like to travel?</h3>
              <ButtonList items={[...destinations.map((d) => [d.slug, d.name]), ['unsure', 'I’m not sure yet']]} onChoose={(slug, name) => { setForm((f) => ({ ...f, destination: slug === 'unsure' ? 'Not sure yet' : name, destination_slug: slug === 'unsure' ? '' : slug })); setScreen('trip-product'); }} />
            </>}
            {screen === 'trip-product' && <>
              <h3 className="mb-3 font-semibold text-eco-ink">What are you looking for?</h3>
              <ButtonList items={PRODUCTS.map((item) => [item, item])} onChoose={(value) => { set('product', value); setScreen('trip-details'); }} />
            </>}
            {screen === 'trip-details' && <div className="grid gap-3">
              <h3 className="font-semibold text-eco-ink">Tell us about the trip</h3>
              <label className="text-xs font-semibold text-eco-ink/65">Preferred travel date<input type="date" value={form.travel_date} onChange={(e) => set('travel_date', e.target.value)} className="eco-input mt-1 w-full" /></label>
              <label className="text-xs font-semibold text-eco-ink/65">Number of travellers *<input required min="1" type="number" value={form.travelers} onChange={(e) => set('travelers', e.target.value)} className="eco-input mt-1 w-full" /></label>
              <label className="text-xs font-semibold text-eco-ink/65">Preferred language<select value={form.preferred_language} onChange={(e) => set('preferred_language', e.target.value)} className="eco-input mt-1 w-full"><option>Portuguese</option><option>English</option><option>German</option><option>French</option></select></label>
              <label className="text-xs font-semibold text-eco-ink/65">Interests or preferences<textarea rows="3" value={form.interests} onChange={(e) => set('interests', e.target.value)} className="eco-input mt-1 w-full" placeholder="Nature, culture, beaches…" /></label>
              <button type="button" disabled={!form.travelers} onClick={() => setScreen('trip-contact')} className="eco-btn-primary justify-center disabled:opacity-50">Continue</button>
            </div>}
            {screen === 'trip-contact' && <ContactFields form={form} set={set} onContinue={() => setScreen('trip-summary')} />}
            {screen === 'trip-summary' && <Summary title="Review your request" rows={[['Destination', form.destination], ['Looking for', form.product], ['Date', form.travel_date || 'Flexible'], ['Travellers', form.travelers], ['Language', form.preferred_language], ['Interests', form.interests || '—'], ['Name', form.name], ['Email', form.email]]} form={form} set={set} busy={busy} onSubmit={submitTrip} />}

            {screen === 'booking' && <div className="grid gap-3">
              <h3 className="font-semibold text-eco-ink">Please enter your Eco Collection Booking or Request ID.</h3>
              <p className="text-xs leading-relaxed text-eco-ink/60">An ID identifies a request but never grants access. We’ll take you to the secure traveller access flow.</p>
              <input value={form.reference} onChange={(e) => set('reference', e.target.value)} className="eco-input" placeholder="ECO-STP-XXXX" aria-label="Booking or Request ID" />
              <button type="button" disabled={!form.reference.trim()} onClick={() => go('/my-trip/access')} className="eco-btn-primary justify-center disabled:opacity-50">Continue securely</button>
            </div>}

            {screen === 'agency' && <><h3 className="mb-3 font-semibold text-eco-ink">How would you like to work with Eco Collection?</h3><ButtonList items={AGENCY_ACTIONS} onChoose={chooseAgency} /></>}
            {screen === 'agency-form' && <BusinessForm title="Tell us about your agency" form={form} set={set} busy={busy} onSubmit={submitAgency} />}

            {screen === 'supplier' && <><h3 className="mb-3 font-semibold text-eco-ink">What type of partner are you?</h3><ButtonList items={SUPPLIER_TYPES.map((item) => [item, item])} onChoose={(value) => { set('supplier_type', value); setScreen('supplier-action'); }} /></>}
            {screen === 'supplier-action' && <><h3 className="mb-3 font-semibold text-eco-ink">What would you like to do?</h3><ButtonList items={SUPPLIER_ACTIONS} onChoose={chooseSupplierAction} /></>}
            {screen === 'supplier-form' && <BusinessForm title="Apply to join the Eco Collection network" form={form} set={set} busy={busy} onSubmit={submitSupplier} consent={false} />}

            {screen === 'success' && <div className="rounded-2xl bg-white p-6 text-center shadow-sm" data-testid="eco-assist-success"><Check className="mx-auto text-eco-green" size={34} /><h3 className="mt-3 text-lg font-semibold text-eco-ink">Request sent</h3>{reference && <p className="mt-2 text-sm font-semibold text-eco-green">Reference: {reference}</p>}<p className="mt-2 text-sm text-eco-ink/60">The Eco Collection team will review your request. Availability is not confirmed until our team contacts you.</p></div>}
            {error && <p role="alert" className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          </div>

          <footer className="flex items-center justify-between border-t border-eco-ink/10 bg-white px-4 py-2.5">
            <span className="text-[10px] font-medium text-eco-ink/40">Powered by NEXIS Assist</span>
            <button type="button" onClick={restart} className="inline-flex min-h-10 items-center gap-1 text-xs font-semibold text-eco-green" aria-label="Restart conversation"><RefreshCw size={13} /> Restart</button>
          </footer>
        </section>
      )}
      {!open && invitationVisible && (
        <div role="status" className="eco-assist-invitation pointer-events-none absolute bottom-[4.5rem] right-0 w-max max-w-[calc(100vw-2rem)] rounded-2xl rounded-br-sm border border-eco-ink/10 bg-white px-3.5 py-2 text-xs font-semibold text-eco-ink shadow-[0_12px_32px_rgba(18,46,38,.16)] sm:bottom-[5.5rem] sm:text-sm" data-testid="eco-assist-invitation">
          <span className="sm:hidden">Need help planning?</span>
          <span className="hidden sm:inline">Planning an island trip?</span>
        </div>
      )}
      <button type="button" onClick={toggle} aria-label={open ? 'Close Eco Assist' : 'Open Eco Assist'} aria-expanded={open} className={`ml-auto place-items-center bg-eco-green text-white shadow-[0_10px_30px_rgba(18,46,38,.28)] transition hover:-translate-y-0.5 hover:bg-eco-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco-gold focus-visible:ring-offset-2 ${open ? 'grid h-14 w-14 rounded-full' : 'relative grid h-16 w-16 rounded-[1.35rem] pt-6 sm:h-[4.5rem] sm:w-[4.5rem] sm:pt-7'}`} data-testid="eco-assist-launcher">
        {open ? <X size={22} /> : <><EcoAssistParrot className="absolute -top-7 left-1/2 h-14 w-12 -translate-x-1/2 sm:-top-8 sm:h-16 sm:w-14" /><span className="text-[10px] font-bold tracking-wide sm:text-[11px]">Eco Assist</span></>}
      </button>
    </div>
  );
}

function ContactFields({ form, set, onContinue }) {
  return <div className="grid gap-3"><h3 className="font-semibold text-eco-ink">How can we contact you?</h3>
    <input required value={form.name} onChange={(e) => set('name', e.target.value)} className="eco-input" placeholder="Name *" aria-label="Name" />
    <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="eco-input" placeholder="Email *" aria-label="Email" />
    <input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className="eco-input" placeholder="Phone / WhatsApp (optional)" aria-label="Phone or WhatsApp" />
    <button type="button" disabled={!form.name.trim() || !form.email.includes('@')} onClick={onContinue} className="eco-btn-primary justify-center disabled:opacity-50">Review request</button>
  </div>;
}

function Summary({ title, rows, form, set, busy, onSubmit }) {
  return <div className="grid gap-3"><h3 className="font-semibold text-eco-ink">{title}</h3><dl className="rounded-xl bg-white p-4 text-sm shadow-sm">{rows.map(([label, value]) => <div key={label} className="grid grid-cols-[6.5rem_1fr] gap-2 border-b border-eco-ink/5 py-1.5 last:border-0"><dt className="text-eco-ink/50">{label}</dt><dd className="font-medium break-words">{value}</dd></div>)}</dl>
    <label className="flex items-start gap-2 text-xs leading-relaxed text-eco-ink/65"><input type="checkbox" checked={form.consent_terms} onChange={(e) => set('consent_terms', e.target.checked)} className="mt-0.5" />I agree to the Terms and Privacy Policy for this request.</label>
    <button type="button" disabled={!form.consent_terms || busy} onClick={onSubmit} className="eco-btn-primary justify-center disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Send request</>}</button>
  </div>;
}

function BusinessForm({ title, form, set, busy, onSubmit, consent = true }) {
  const ready = form.company.trim() && form.contact.trim() && form.country.trim() && form.email.includes('@') && (!consent || form.consent_terms);
  return <div className="grid gap-3"><h3 className="font-semibold text-eco-ink">{title}</h3>
    <input value={form.company} onChange={(e) => set('company', e.target.value)} className="eco-input" placeholder="Company / agency name *" aria-label="Company or agency name" />
    <input value={form.contact} onChange={(e) => set('contact', e.target.value)} className="eco-input" placeholder="Contact person *" aria-label="Contact person" />
    <input value={form.country} onChange={(e) => set('country', e.target.value)} className="eco-input" placeholder="Country *" aria-label="Country" />
    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="eco-input" placeholder="Business email *" aria-label="Business email" />
    <input type="url" value={form.website} onChange={(e) => set('website', e.target.value)} className="eco-input" placeholder="Website (optional)" aria-label="Website" />
    <textarea rows="3" value={form.message} onChange={(e) => set('message', e.target.value)} className="eco-input" placeholder="Request / message" aria-label="Request or message" />
    {consent && <label className="flex items-start gap-2 text-xs leading-relaxed text-eco-ink/65"><input type="checkbox" checked={form.consent_terms} onChange={(e) => set('consent_terms', e.target.checked)} className="mt-0.5" />I agree to the Terms and Privacy Policy for this enquiry.</label>}
    <button type="button" disabled={!ready || busy} onClick={onSubmit} className="eco-btn-primary justify-center disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Send enquiry</>}</button>
  </div>;
}
